import React, { useEffect, useState } from 'react';
import { format, parseISO, startOfMonth } from 'date-fns';
import { CalendarCheckIcon, CheckIcon, InfoIcon, SendIcon, TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSelfService } from '../../hooks/useSelfService';
import { displayName } from '../../utils/employees';
import { TODAY, formatDays, formatRange } from '../../utils/format';
import { countRequestDays } from '../../utils/holidays';
import { hrActions } from '../../utils/hrStore';
import { trimNumber } from '../../utils/selfService';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { RangeCalendar } from './RangeCalendar';
interface RequestTimeOffDialogProps {
  open: boolean;
  onClose: () => void;
}
const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');
export function RequestTimeOffDialog({
  open,
  onClose
}: RequestTimeOffDialogProps) {
  const {
    me,
    policy,
    manager,
    available,
    holidays
  } = useSelfService();
  const assigned = policy ? [policy] : [];
  const [policyId, setPolicyId] = useState(policy?.id ?? '');
  const [month, setMonth] = useState(startOfMonth(TODAY));
  const [start, setStart] = useState<string | undefined>();
  const [end, setEnd] = useState<string | undefined>();
  const [halfFirst, setHalfFirst] = useState(false);
  const [halfLast, setHalfLast] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!open) return;
    setPolicyId(policy?.id ?? '');
    setMonth(startOfMonth(TODAY));
    setStart(undefined);
    setEnd(undefined);
    setHalfFirst(false);
    setHalfLast(false);
    setNote('');
  }, [open, policy?.id]);
  const select = (iso: string) => {
    if (!start || end) {
      setStart(iso);
      setEnd(undefined);
      return;
    }
    if (iso < start) {
      setStart(iso);
      return;
    }
    if (iso === start) return;
    setEnd(iso);
  };
  const rangeEnd = end ?? start;
  const multiDay = Boolean(start && rangeEnd && rangeEnd !== start);
  const breakdown = start && rangeEnd ? countRequestDays(start, rangeEnd, holidays, halfFirst, multiDay && halfLast) : null;
  const requested = breakdown?.days ?? 0;
  const remaining = available - requested;
  const exceeds = requested > available;
  const canSubmit = Boolean(start && policyId) && requested > 0 && !exceeds;
  const managerName = manager ? displayName(manager) : 'your manager';
  const submit = () => {
    if (!start || !rangeEnd || !canSubmit) return;
    hrActions.addRequest({
      id: `req-${Date.now()}`,
      employeeId: me.id,
      start,
      end: rangeEnd,
      days: requested,
      policyId,
      note: note.trim() || 'No note added.',
      submitted: TODAY_ISO,
      balanceBefore: available,
      status: 'pending'
    });
    toast.success(`Request sent to ${managerName}`, {
      description: `${formatRange(start, rangeEnd)} · ${formatDays(requested)} · Pending approval`
    });
    onClose();
  };
  return <Modal open={open} onClose={onClose} size="xl" title="Request time off" description={`Requests go to ${managerName} for approval. Weekends and company holidays are never counted.`} footer={<>
          <Button onClick={onClose} data-id="element-817" data-mp="1p858lpocn5b9">Cancel</Button>
          <Button variant="primary" icon={SendIcon} onClick={submit} disabled={!canSubmit} data-id="element-818" data-mp="1p5k6czk52ncz">
            Submit request
          </Button>
        </>} data-id="element-816" data-mp="3jlrascsacoqi">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-819" data-mp="2hxc7wqvmnyxk">
        <div data-id="element-820" data-mp="2mvx9atmm8onl">
          <fieldset data-id="element-821" data-mp="2muced2we33p7">
            <legend className="text-xs font-medium text-slate-600" data-id="element-822" data-mp="2muvmxt05gfqk">Policy</legend>
            {assigned.length === 0 ? <p className="mt-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-200" data-id="element-823" data-mp="28xp98cua9787">You aren't assigned to a time off policy yet. Contact HR.</p> : <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-824" data-mp="xittsontb1ql">
                {assigned.map(p => {
              const selected = p.id === policyId;
              return <button key={p.id} type="button" onClick={() => setPolicyId(p.id)} aria-pressed={selected} className={`flex items-center gap-3 rounded-xl px-4 py-3 text-left ring-1 ring-inset transition ${selected ? 'bg-azure/[0.06] ring-2 ring-azure' : 'ring-slate-200 hover:ring-slate-300'}`} data-id="element-825" data-mp="1hr0eqjjtja8n">
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${selected ? 'bg-azure text-white' : 'ring-1 ring-slate-300'}`} data-id="element-826" data-mp="1vogh9jyzujv4">
                        {selected && <CheckIcon className="h-3 w-3" data-id="element-827" data-mp="1mok1y8yhuxcq" />}
                      </span>
                      <span data-id="element-828" data-mp="1mqmt4wbduwj1">
                        <span className="block text-sm font-medium text-navy" data-id="element-829" data-mp="fb8a7c2d2dug">{p.name}</span>
                        <span className="block text-xs text-muted" data-id="element-830" data-mp="b8lolswa9p5p">{trimNumber(available)} days available</span>
                      </span>
                    </button>;
            })}
              </div>}
          </fieldset>

          <div className="mt-6 rounded-2xl p-4 ring-1 ring-inset ring-slate-200" data-id="element-831" data-mp="2qvzbocww32a9">
            <RangeCalendar month={month} onMonthChange={setMonth} start={start} end={end} onSelect={select} holidays={holidays} minIso={TODAY_ISO} data-id="element-832" data-mp="2vvlqgqsd8ql6" />
          </div>
          <p className="mt-2 text-xs text-muted" data-id="element-833" data-mp="35nre5obl4luk">{!start ? 'Pick your first day off.' : !end ? 'Pick a last day, or submit for a single day.' : 'Click any date to start a new range.'}</p>
        </div>

        <div className="space-y-5" data-id="element-834" data-mp="3enegccc51voo">
          <div className="rounded-2xl bg-canvas p-4" data-id="element-835" data-mp="3eofsyl8syq8i">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-836" data-mp="3jn978fvwyl9q">Selected dates</p>
            <p className="mt-1 text-base font-semibold text-navy" data-id="element-837" data-mp="1kaewu84n0hc4">
              {start && rangeEnd ? `${format(parseISO(start), 'EEE, MMM d')}${multiDay ? ` – ${format(parseISO(rangeEnd), 'EEE, MMM d')}` : ''}` : 'None yet'}
            </p>
            {breakdown && (breakdown.weekendDays > 0 || breakdown.holidays.length > 0) && <ul className="mt-3 space-y-1 text-xs text-slate-600" data-id="element-838" data-mp="2nmdkqg747drp">
                {breakdown.weekendDays > 0 && <li data-id="element-839" data-mp="31lllvemu8vyj">{breakdown.weekendDays} weekend {breakdown.weekendDays === 1 ? 'day' : 'days'} · not counted</li>}
                {breakdown.holidays.map(h => <li key={h.id} className="text-[#c25a00]" data-id="element-840" data-mp="3bhg9ko5x5h86">
                    {format(parseISO(h.date), 'MMM d')} · {h.name} — {h.halfDay ? 'half day (0.5 counted)' : 'holiday, not counted'}
                  </li>)}
              </ul>}
          </div>

          <div className="space-y-2.5" data-id="element-841" data-mp="3g8olbquq1eoi">
            <label className={`flex items-center gap-3 text-sm ${start ? 'cursor-pointer text-slate-700' : 'cursor-not-allowed text-slate-400'}`} data-id="element-842" data-mp="3g9q46hhzb5gs">
              <input type="checkbox" checked={halfFirst} disabled={!start} onChange={e => setHalfFirst(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-azure focus:ring-azure" data-id="element-843" data-mp="3pa4uxwdppcro" />
              {multiDay ? 'First day is a half day' : 'Half day only'}
            </label>
            {multiDay && <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-700" data-id="element-844" data-mp="2sw2sical96x">
                <input type="checkbox" checked={halfLast} onChange={e => setHalfLast(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-azure focus:ring-azure" data-id="element-845" data-mp="7qedoe9vdyhk" />
                Last day is a half day
              </label>}
          </div>

          <div data-id="element-846" data-mp="3g70w9gs7tbaf">
            <label htmlFor="pto-note" className="block text-xs font-medium text-slate-600" data-id="element-847" data-mp="3g7iz4w9dunrb">
              Note to {managerName} <span className="text-muted" data-id="element-848" data-mp="3l5anjgatjrqr">(optional)</span>
            </label>
            <textarea id="pto-note" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Family trip — I'll hand off open files to the team." className="mt-1.5 w-full resize-none rounded-xl border-0 px-3.5 py-2.5 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-849" data-mp="376kpnyg6do1c" />
          </div>

          <div className={`rounded-2xl p-4 ring-1 ring-inset ${exceeds ? 'bg-rose-50 ring-rose-200' : 'bg-azure/[0.06] ring-azure/20'}`} aria-live="polite" data-id="element-850" data-mp="3cq6bhw6rwmqw">
            <p className="flex items-center gap-2 text-sm font-semibold text-navy" data-id="element-851" data-mp="38qdxv52ivqeo">
              <CalendarCheckIcon className="h-4 w-4 text-azure" aria-hidden="true" data-id="element-852" data-mp="33vrp79ntsbya" />
              {trimNumber(requested)} business {requested === 1 ? 'day' : 'days'} requested
            </p>
            <p className="mt-1 text-xs text-slate-600" data-id="element-853" data-mp="2u1huhlbev1ke">
              {trimNumber(requested)} requested · {trimNumber(available)} available ·{' '}
              <span className={`font-semibold ${exceeds ? 'text-rose-600' : 'text-navy'}`} data-id="element-854" data-mp="2kygrhthudqky">{trimNumber(remaining)} remaining after approval</span>
            </p>
            {exceeds && <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-rose-700" data-id="element-855" data-mp="14i9kysur5b64">
                <TriangleAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-856" data-mp="19hvy72akyrex" />
                This is more than your available balance. Shorten the range or add a half day to submit.
              </p>}
          </div>
          <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted" data-id="element-857" data-mp="gu9xq2wng2wa">
            <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-858" data-mp="lstiw13f5anl" />
            Available = your balance minus time already scheduled or pending.
          </p>
        </div>
      </div>
    </Modal>;
}